/**
 * @fileOverview Loads city data and passes it to child component.
 * Loads the following info and passes down as props
 * - clients
 * - campaigns
 * - locations
 */

import React, { useState, useEffect, createContext, useCallback } from 'react'
import { getCommonCityData } from './../../../Module/graphQL'
import { authorization } from 'Module/Auth/Auth'
import { normalizeGraphQLConnection } from '../../../shared/graphql/utils'

export const CityDataContext = createContext({})
export const CityDataContextProvider = CityDataContext.Provider

const CityData = props => {
  const { children, cityId, ...otherProps } = props

  const [cityData, setCityData] = useState({
    clients: [],
    campaigns: [],
    locations: [],
  })

  // Will be set to true once the filter info has been requested
  const [requestedFilterInfo, setRequestedFilterInfo] = useState(false)
  const [loaded, setLoaded] = useState(false)

  // This fetches all the data we need for the global planning filters. For
  // example we need to know all the campaigns and clients that operate in the
  // current city
  useEffect(() => {
    if (requestedFilterInfo) return
    setRequestedFilterInfo(true)
    fetchCityData({ cityId })
  }, [cityId])

  const fetchCityData = async ({ cityId }) => {
    const authToken = await authorization.getAPIKey()
    const response = await getCommonCityData(authToken, { cityId })
    const result = await response.json()
    checkErrors(result)
    const cityData = formatRawCityData({ data: result.data.city })
    setCityData(cityData)
    setLoaded(true)
  }
  return (
    <CityDataContextProvider
      value={{
        props: {
          ...otherProps,
          cityId,
          ...cityData,
          cityDataLoaded: loaded,
        },
      }}
    >
      {children}
    </CityDataContextProvider>
  )
}

export default CityData

const cache = {}
export const useCityData = (cache => (cityId, isRefresh = false) => {
  const [loaded, setLoaded] = useState(false)

  const [cityData, setCityData] = useState(
    cache[cityId] || {
      clients: [],
      campaigns: [],
      locations: [],
      country: {},
    },
  )

  useEffect(() => {
    if (!cache[cityId] || isRefresh) {
      cache[cityId] = 'load'
      ;(async () => {
        cache[cityId] = await fetchCityData({ cityId })
        setCityData(cache[cityId])
        setLoaded(true)
      })()
    } else if (cache[cityId] === 'load') {
      waitForData(cityId, data => {
        setCityData(data)
        setLoaded(true)
      })
    } else {
      setCityData(cache[cityId])
      setLoaded(true)
    }
  }, [cityId])

  const waitForData = (cityId, cb) => {
    setTimeout(() => {
      if (cache[cityId] && cache[cityId] !== 'load') {
        return cb(cache[cityId])
      } else {
        return waitForData(cityId)
      }
    }, 1000)
  }

  const fetchCityData = async ({ cityId }) => {
    const authToken = await authorization.getAPIKey()
    const response = await getCommonCityData(authToken, { cityId })
    const result = await response.json()
    checkErrors(result)
    return formatRawCityData({ data: result.data.city })
  }

  const dataRefresh = useCallback(data => {
    if (cache[cityId]) {
      cache[cityId] = {
        ...cache[cityId],
        ...data,
      }
    }
  }, [])

  return { loaded, data: cityData, dataRefresh }
})(cache)

const formatRawCityData = ({ data }) => ({
  clients: normalizeGraphQLConnection(data.clients),
  locations: normalizeGraphQLConnection(data.locations),
  campaigns: normalizeGraphQLConnection(data.campaigns),
  cityLocation: data.location,
  newcomersMissionsLimit: data.newcomersMissionsLimitV2,
  recruitmentOnHold: data.recruitmentOnHold,
  name: data.name,
  lunchCheckInTime: data.lunchCheckInTime,
  afternoonCheckInTime: data.afternoonCheckInTime,
  payHourly: data.payHourly,
  timezone: data.timezone,
  status: data.statusV2,
  cbdRadius: data.cbdRadius,
  pickupInformationURL: data.pickupInformationURL,
  morningCheckInTimeBefore: data.morningCheckInTimeBefore,
  morningCheckInTimeAfter: data.morningCheckInTimeAfter,
  lunchCheckInTimeBefore: data.lunchCheckInTimeBefore,
  lunchCheckInTimeAfter: data.lunchCheckInTimeAfter,
  afternoonCheckInTimeBefore: data.afternoonCheckInTimeBefore,
  afternoonCheckInTimeAfter: data.afternoonCheckInTimeAfter,
  checkinRadius: data.checkinRadius,
  surgeInterval: data.surgeInterval,
  teamLeaderEnabled: data.teamLeaderEnabled,
  country: data.country,
  cityCenterLocation: data.location,
  recruitmentCalendarId: data.recruitmentCalendarId,
  onboardingLocation: data.onboardingLocation,
  onboardingUrl: data.onboardingUrl,
  spies: normalizeGraphQLConnection(data.spies),
  spySettings: data.spySettings,
})

const checkErrors = result => {
  if (result['errorMessage']) throw new Error(result['errorMessage'])
  if (result.errors && result.errors[0]) {
    throw new Error(result.errors[0].message)
  }
}



// WEBPACK FOOTER //
// ./src/Containers/City/DataHOCs/CityDataHOC.js